import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPricingPlansSettingsV1PricingPlansSettings from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.http.js';

export interface PricingPlansSettings {
  /**
   * Revision number, which increments by 1 each time the PricingPlans settings are updated. To prevent conflicting changes, the current revision must be passed when updating the PricingPlans settings.
   * Ignored when creating a PricingPlans settings.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the PricingPlansSettings was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the PricingPlansSettings was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Site's taxable address, which determines how tax is calculated for pricing plans. */
  taxableAddress?: TaxableAddressWithLiterals;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /**
   * Platform fee percentage rate based on premium plan
   * @decimalValue options { gte:0, maxScale:2 }
   * @readonly
   */
  platformFeeRate?: string | null;
  /**
   * Indicates whether platform fee is enabled.
   * @internal
   * @readonly
   */
  platformFeeEnabled?: boolean | null;
  /**
   * Guest checkout settings.
   * @internal
   */
  guestCheckoutSettings?: GuestCheckoutSettings;
  /**
   * Invoice settings.
   * @internal
   */
  invoiceSettings?: InvoiceSettings;
}

export enum TaxableAddress {
  BILLING = 'BILLING',
  BUSINESS = 'BUSINESS',
}

/** @enumType */
export type TaxableAddressWithLiterals =
  | TaxableAddress
  | 'BILLING'
  | 'BUSINESS';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GuestCheckoutSettings {
  /**
   * Whether guest checkout is enabled.
   * @internal
   */
  enabled?: boolean | null;
}

export interface InvoiceSettings {
  /**
   * Whether to automatically send an invoice after online payment.
   * @internal
   */
  sendInvoiceOnPayment?: boolean | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface GetPricingPlansSettingsRequest {}

export interface GetPricingPlansSettingsResponse {
  /** Pricing Plans settings */
  pricingPlansSettings?: PricingPlansSettings;
}

export interface UpdatePricingPlansSettingsRequest {
  /** PricingPlansSettings to be updated, may be partial. */
  pricingPlansSettings: PricingPlansSettings;
  /**
   * Set of fields to update.
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdatePricingPlansSettingsResponse {
  /** Updated PricingPlansSettings. */
  pricingPlansSettings?: PricingPlansSettings;
}

export interface FeatureEvent extends FeatureEventEventOneOf {
  /**
   * Information about an event that makes a feature eligible to the user.
   * Triggered for example, for new features or when a feature is reassigned
   * to an account or a site.
   */
  enabled?: FeatureEnabled;
  /**
   * Information about an event that disables a feature for the user.
   * Triggered for example, when a feature is unassigned from a site,
   * reassigned to a different site, or the user switched to a different contract.
   */
  disabled?: FeatureDisabled;
  /**
   * Information about an event that updates a feature. An `updated` event
   * is triggered for example by the
   * [Report Quota Usage](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/report-quota-usage)
   * and [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter)
   * endpoints.
   */
  updated?: FeatureUpdated;
  /**
   * Information about an event that cancels a feature for the user.
   * Triggered for example, when a feature is canceled, transferred to
   * another account, or the user switched to a different contract.
   */
  cancelled?: FeatureCancelled;
  /**
   * Information about a quota usage change for a feature. Triggered when
   * quota usage is reported via Report Quota Usage endpoint. Includes
   * before/after usage and remaining values.
   */
  quotaUsageChanged?: FeatureQuotaUsageChanged;
  /**
   * Timestamp of the event in
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   */
  timestamp?: Date | null;
}

/** @oneof */
export interface FeatureEventEventOneOf {
  /**
   * Information about an event that makes a feature eligible to the user.
   * Triggered for example, for new features or when a feature is reassigned
   * to an account or a site.
   */
  enabled?: FeatureEnabled;
  /**
   * Information about an event that disables a feature for the user.
   * Triggered for example, when a feature is unassigned from a site,
   * reassigned to a different site, or the user switched to a different contract.
   */
  disabled?: FeatureDisabled;
  /**
   * Information about an event that updates a feature. An `updated` event
   * is triggered for example by the
   * [Report Quota Usage](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/report-quota-usage)
   * and [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter)
   * endpoints.
   */
  updated?: FeatureUpdated;
  /**
   * Information about an event that cancels a feature for the user.
   * Triggered for example, when a feature is canceled, transferred to
   * another account, or the user switched to a different contract.
   */
  cancelled?: FeatureCancelled;
  /**
   * Information about a quota usage change for a feature. Triggered when
   * quota usage is reported via Report Quota Usage endpoint. Includes
   * before/after usage and remaining values.
   */
  quotaUsageChanged?: FeatureQuotaUsageChanged;
}

/** Feature created or enabled after disabled state */
export interface FeatureEnabled extends FeatureEnabledReasonOneOf {
  /** Information about a transfer from another account. */
  transferredFromAnotherAccount?: TransferredFromAnotherAccountReason;
  /** Information about a transfer from another site. */
  reassignedFromSite?: ReassignedFromSiteReason;
  /** Information about a feature that hadn't been assigned to site. */
  assignedFromFloating?: AssignedFromFloatingReason;
  /** Information about the new feature. */
  newFeature?: NewFeatureReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the manually created features. */
  manualFeatureCreation?: ManualFeatureCreationReason;
  /** Information about a feature that was migrated from legacy. */
  migratedFromLegacy?: MigratedFromLegacyReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Enabled feature. */
  feature?: Feature;
  /**
   * Information about a transfer from another account.
   * __Deprecated__. Use `reason.transferred_from_another_account` instead.
   * @format GUID
   */
  transferredFromAccount?: string | null;
  /**
   * Information about a transfer from another site.
   * __Deprecated__. Use `reason.reassigned_from_site` instead.
   * @format GUID
   */
  reassignedFromMetasite?: string | null;
}

/** @oneof */
export interface FeatureEnabledReasonOneOf {
  /** Information about a transfer from another account. */
  transferredFromAnotherAccount?: TransferredFromAnotherAccountReason;
  /** Information about a transfer from another site. */
  reassignedFromSite?: ReassignedFromSiteReason;
  /** Information about a feature that hadn't been assigned to site. */
  assignedFromFloating?: AssignedFromFloatingReason;
  /** Information about the new feature. */
  newFeature?: NewFeatureReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the manually created features. */
  manualFeatureCreation?: ManualFeatureCreationReason;
  /** Information about a feature that was migrated from legacy. */
  migratedFromLegacy?: MigratedFromLegacyReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
}

export interface Feature extends FeatureQuantityInfoOneOf {
  /**
   * Deprecated. Use `enabled` instead.
   * @deprecated
   */
  booleanFeature?: BooleanFeature;
  /**
   * Deprecated. Use `quotaInfo` instead.
   * @deprecated
   */
  quotaFeature?: QuotaFeature;
  /**
   * ID of the feature. __Note:__ Isn't unique. For example, all features that
   * are available to free Wix accounts or site in some capacity have
   * `{"id": "DEFAULT"}`. Use `uniqueName` as unique identifier for a feature.
   * @readonly
   */
  _id?: string;
  /**
   * Unique name of the feature. Only lower case letters, numbers, and dashes
   * `-` are supported. Used in the endpoints of the
   * [Features Manager API](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/introduction)
   * to specify the feature. Not visible to customers. We recommend to start
   * the unique name with a prefix describing your organization or Wix company.
   * For example, `bookings` or `crm`.
   *
   * Min: 2 characters
   * Max: 50 characters
   * @minLength 2
   * @maxLength 50
   */
  uniqueName?: string;
  /**
   * Information about whether the feature belongs to a Wix account or site.
   * Account features have `context.userId`. Site features have `context.metaSiteId` in case
   * they're assigned to a specific site. Site features that aren't assigned to
   * a specific site have neither ID.
   */
  context?: FeatureContext;
  /**
   * Deprecated.
   * @readonly
   * @deprecated
   */
  createdAt?: Date | null;
  /**
   * Deprecated.
   * @readonly
   * @deprecated
   */
  updatedAt?: Date | null;
  /**
   * Aggregated information about how often customers can use the feature.
   * Available only for quota features.
   */
  quotaInfo?: QuotaInfo;
  /**
   * Whether the customer is currently allowed to use the feature.
   * `true` means that the customer can use the feature. This means a boolean
   * feature is active or a quota feature has remaining usage.
   * `false` means that the customer can't use the feature.
   * This means a boolean feature isn't active or a quota feature doesn't
   * have remaining usage.
   */
  enabled?: boolean;
  /**
   * ID of the [subscription](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/subscription-object)
   * to which the feature instance belongs.
   * @format GUID
   */
  subscriptionId?: string | null;
  /**
   * Metadata of the feature. Wix Premium uses the metadata object to indicate
   * that customers who purchase a product with the feature also get
   * access to an additional product. For these bundled products `metadata`
   * looks like this: `{"tpa": "{"appDefId": "sample-app-def-id-1234567890", "vendorProductId": "sample-productId"}}"`.
   * But you can use the `metadata` property for other purposes, too.
   */
  metadata?: Record<string, string>;
}

/** @oneof */
export interface FeatureQuantityInfoOneOf {
  /**
   * Deprecated. Use `enabled` instead.
   * @deprecated
   */
  booleanFeature?: BooleanFeature;
  /**
   * Deprecated. Use `quotaInfo` instead.
   * @deprecated
   */
  quotaFeature?: QuotaFeature;
}

/**
 * Context this feature is currently connected to.
 * Note: Do not confuse with feature scope which is configured in the product catalog
 * and defines in which context the product can be used
 */
export interface FeatureContext {
  /**
   * ID of the Wix account that the feature instance belongs to.
   * Available for both site and account level feature instances.
   * @format GUID
   */
  userId?: string;
  /**
   * ID of the meta site that the feature instance is assigned to.
   * Only available for site level feature instances that are assigned to a Wix
   * site. Not available for account level and unassigned site level feature
   * instances.
   * @format GUID
   */
  metaSiteId?: string | null;
}

/**
 * A feature that can be either "enabled" or "disabled". The default/freemium setting is always OFF, and the premium setting is always ON (meaning, unlimited usage without tracking).
 * A boolean feature is similar to a quantitive feature with a default limit of 0 and UNLIMITED premium limit (although a bit simplified).
 */
export interface BooleanFeature {}

/** A feature with a periodic usage limitation. The default limit is defined in the Feature Spec, the Premium limits are defined in the respective ProductFeature. */
export interface QuotaFeature {
  /** Default (or Freemium) quota limitation. if left undefined the free feature has unlimited amount. */
  limit?: string | null;
  /** Periodic time-frame to reset the usage counter. You may use NO_PERIOD if counter shouldn't be reset. */
  period?: FeaturePeriodWithLiterals;
  /** Usage measurement units (seconds? MBs? unitless?). Usage reported will be counted in multiples of this basic unit. */
  units?: string | null;
}

/** Determines the reset cycle of the feature usage. */
export enum FeaturePeriod {
  NO_PERIOD = 'NO_PERIOD',
  MILLISECOND = 'MILLISECOND',
  SECOND = 'SECOND',
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type FeaturePeriodWithLiterals =
  | FeaturePeriod
  | 'NO_PERIOD'
  | 'MILLISECOND'
  | 'SECOND'
  | 'MINUTE'
  | 'HOUR'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface QuotaInfo {
  /**
   * Aggregation of how often the customer is allowed to use the feature at the time of the request.
   * `null` means that the customer has unlimited access to the feature.
   */
  limit?: string | null;
  /**
   * Deprecated because a feature can have multiple periods defined. Use `period` in `breakdown` entries instead.
   * Time frame for the usage limitation. `NO_PERIOD` means that `remainingUsage`
   * isn't automatically reset to the feature's `limit` after a specific period.
   * You may still manually call
   * [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter).
   * @deprecated
   * @replacedBy breakdown.period
   * @targetRemovalDate 2026-03-31
   */
  period?: FeaturePeriodWithLiterals;
  /**
   * Aggregation of how often the customer has used the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will contain the value `-1`.
   */
  currentUsage?: string;
  /**
   * Aggregation of how often the customer can still use the feature.
   * `null` means that the customer has unlimited access to the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will not be populated.
   */
  remainingUsage?: string | null;
  /**
   * Breakdown of quota information entries, entry per feature instance.
   * @maxSize 1000
   */
  breakdown?: QuotaInfoEntry[];
}

export interface QuotaInfoEntry {
  /**
   * If quota is of free feature, the id would be empty (None).
   * If quota is of premium feature, this is the ID of the feature instance.
   * @format GUID
   */
  featureInstanceId?: string | null;
  /**
   * How often the customer is allowed to use the feature during the specified
   * period. `null` means that the customer has unlimited access to the feature.
   */
  limit?: string | null;
  /**
   * Time frame for the usage limitation. `NO_PERIOD` means that `remainingUsage`
   * isn't automatically reset to the feature's `limit` after a specific period.
   * You may still manually call
   * [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter).
   */
  period?: FeaturePeriodWithLiterals;
  /**
   * How often the customer has used the feature during the current
   * period.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will contain the value `-1`.
   */
  currentUsage?: string;
  /**
   * How often the customer can still use the feature during the current
   * period. `null` means that the customer has unlimited access to the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will not be populated.
   */
  remainingUsage?: string | null;
  /**
   * ID of the subscription to which the feature instance belongs.
   * @format GUID
   */
  subscriptionId?: string | null;
  /**
   * Reset date of the quota.
   * Relevant only for features with a period that is not `NO_PERIOD`, and only for features that are managed by the Quota Manager system.
   */
  resetDate?: Date | null;
}

/** Subscription transferred from another account, features on the current account were enabled. */
export interface TransferredFromAnotherAccountReason {
  /**
   * Information about a transfer from another account.
   * @format GUID
   */
  transferredFromAccount?: string;
}

/** Subscription moved from one site to another in the same account, features enabled on the target site */
export interface ReassignedFromSiteReason {
  /**
   * Information about a transfer from another site.
   * @format GUID
   */
  reassignedFromMetasite?: string;
}

/** Subscription was floating and assigned to site, features enabled on the target site */
export interface AssignedFromFloatingReason {}

/** New subscription created and features created as enabled */
export interface NewFeatureReason {}

/** Subscription was upgraded or downgraded, as a result new features enabled, missing features disabled , quantities are updated */
export interface ContractSwitchedReason {
  /** Classification of the contract switch (upgrade, downgrade, or price increase) */
  classification?: ContractSwitchClassificationWithLiterals;
}

/**
 * Classification of contract switch events for quota reset behavior
 * Needed to determine whether to reset usage and/or reset date for features affected by the contract switch.
 * For example, for an upgrade we want to reset both usage and date.
 * For a downgrade we want to keep the usage and date but update the limit, and for a price increase we want to reset the usage but keep the date.
 */
export enum ContractSwitchClassification {
  /** Unknown or unclassified contract switch */
  UNKNOWN = 'UNKNOWN',
  /** Upgrade - reset usage, reset date, and update limit */
  UPGRADE = 'UPGRADE',
  /** Downgrade - only update limit (no reset) */
  DOWNGRADE = 'DOWNGRADE',
  /** Price increase - reset usage only (not reset date), update limit */
  PRICE_INCREASE = 'PRICE_INCREASE',
}

/** @enumType */
export type ContractSwitchClassificationWithLiterals =
  | ContractSwitchClassification
  | 'UNKNOWN'
  | 'UPGRADE'
  | 'DOWNGRADE'
  | 'PRICE_INCREASE';

/** a call to CreateFeature in features-writer, creates feature that is not attached to subscription */
export interface ManualFeatureCreationReason {}

/** Subscription created due to migration from old premium model */
export interface MigratedFromLegacyReason {}

/** Feature was synced manually, for example by immigrator task */
export interface ManualSyncReason {
  /**
   * Optional description of the manual sync source (e.g. "immigrator", "quota-sync").
   * @maxLength 100
   */
  source?: string | null;
}

/** Feature disabled and can be enabled in the future */
export interface FeatureDisabled extends FeatureDisabledReasonOneOf {
  /** Information about a feature that's no longer assigned to a site. */
  unassingedToFloating?: UnAssingedToFloatingReason;
  /**
   * Information about a feature that's been replaced by a feature from a
   * different subscription.
   */
  replacedByAnotherSubscription?: ReplacedByAnotherSubscriptionReason;
  /**
   * Information about a feature that's been reassigned to a different
   * site.
   */
  reassignedToAnotherSite?: ReassignedToAnotherSiteReason;
  /**
   * Disabled feature. Includes information about the feature's new state,
   * possibly its new context.
   */
  feature?: Feature;
  /**
   * ID of the meta site for which the feature has been disabled.
   * @format GUID
   */
  metaSiteId?: string | null;
}

/** @oneof */
export interface FeatureDisabledReasonOneOf {
  /** Information about a feature that's no longer assigned to a site. */
  unassingedToFloating?: UnAssingedToFloatingReason;
  /**
   * Information about a feature that's been replaced by a feature from a
   * different subscription.
   */
  replacedByAnotherSubscription?: ReplacedByAnotherSubscriptionReason;
  /**
   * Information about a feature that's been reassigned to a different
   * site.
   */
  reassignedToAnotherSite?: ReassignedToAnotherSiteReason;
}

/** Subscription was unassigned from the site and moved into floating state */
export interface UnAssingedToFloatingReason {}

/** Another subscription was assigned to the site, causing existing features on this site to be disabled */
export interface ReplacedByAnotherSubscriptionReason {}

/** Subscription was assigned to another site, causing  features on the origin  site to be disabled. */
export interface ReassignedToAnotherSiteReason {
  /**
   * Information about a transfer to the site.
   * @format GUID
   */
  reassignedToMetasite?: string;
}

/** Feature updated, for example Quota was increased due to upgrade */
export interface FeatureUpdated
  extends FeatureUpdatedPreviousQuantityInfoOneOf,
    FeatureUpdatedReasonOneOf {
  /** Information about a feature that doesn't have a usage quota. */
  booleanFeature?: BooleanFeature;
  /** Information about a feature that has a usage quota. */
  quotaFeature?: QuotaFeature;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /**
   * Updated feature. Includes information about the feature's new state and
   * possibly its new context.
   */
  feature?: Feature;
}

/** @oneof */
export interface FeatureUpdatedPreviousQuantityInfoOneOf {
  /** Information about a feature that doesn't have a usage quota. */
  booleanFeature?: BooleanFeature;
  /** Information about a feature that has a usage quota. */
  quotaFeature?: QuotaFeature;
}

/** @oneof */
export interface FeatureUpdatedReasonOneOf {
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
}

/** Feature was permanently cancelled */
export interface FeatureCancelled extends FeatureCancelledReasonOneOf {
  /** Information about a transfer to the account. */
  transferredToAnotherAccount?: TransferredToAnotherAccountReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the feature cancellation. */
  cancelRequest?: CancelRequestedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Feature was automatically cancelled because its quota was fully consumed. */
  quotaExhausted?: QuotaExhaustedReason;
  /** Canceled feature. */
  feature?: Feature;
  /**
   * Information about a transfer to the account.
   * __Deprecated__. Use `reason.transferred_to_account` instead.
   * @format GUID
   */
  transferredToAccount?: string | null;
}

/** @oneof */
export interface FeatureCancelledReasonOneOf {
  /** Information about a transfer to the account. */
  transferredToAnotherAccount?: TransferredToAnotherAccountReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the feature cancellation. */
  cancelRequest?: CancelRequestedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Feature was automatically cancelled because its quota was fully consumed. */
  quotaExhausted?: QuotaExhaustedReason;
}

/** Subscription was transferred to another account, features in the origin account were cancelled */
export interface TransferredToAnotherAccountReason {
  /**
   * Information about a transfer to the account.
   * @format GUID
   */
  transferredToAccount?: string;
}

/** Cancellation was requested from the subscription manager api, might be a result of billing event, or direct call */
export interface CancelRequestedReason {}

/** Feature was automatically cancelled because its quota was fully consumed (e.g. topup feature) */
export interface QuotaExhaustedReason {}

/** Quota usage changed for a feature. */
export interface FeatureQuotaUsageChanged {
  /** Full feature state before this quota usage report. */
  featureBefore?: Feature;
  /** Full feature state after this quota usage report. */
  featureAfter?: Feature;
}

export interface Empty {}

export interface SubscriptionEvent extends SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
  /** Triggered when a subscription is updated. */
  updated?: SubscriptionUpdated;
  /** Triggered when a Vista reseller subscription is migrated into Wix SBS billing. */
  migratedFromVista?: SubscriptionMigratedFromVista;
  /**
   * Triggered when a Vista migration is reverted, restoring the original
   * reseller billing provider and product.
   */
  vistaMigrationReverted?: SubscriptionVistaMigrationReverted;
  /**
   * ID of the subscription's event.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Date and time of the event in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  eventDate?: Date | null;
}

/** @oneof */
export interface SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
  /** Triggered when a subscription is updated. */
  updated?: SubscriptionUpdated;
  /** Triggered when a Vista reseller subscription is migrated into Wix SBS billing. */
  migratedFromVista?: SubscriptionMigratedFromVista;
  /**
   * Triggered when a Vista migration is reverted, restoring the original
   * reseller billing provider and product.
   */
  vistaMigrationReverted?: SubscriptionVistaMigrationReverted;
}

/** Triggered when a subscription is created. */
export interface SubscriptionCreated {
  /** Created subscription. */
  subscription?: Subscription;
  /** Metadata for the `created` event. */
  metadata?: Record<string, string>;
  /**
   * Subscription reactivation data.
   * A subscription can be reactivated for example if it was incorrectly canceled because of fraud and then reactivated
   * by the billing system
   */
  reactivationData?: ReactivationData;
}

/**
 * A subscription holds information about a Premium product that a Wix account
 * owner has purchased including details about the billing.
 */
export interface Subscription {
  /**
   * ID of the subscription.
   * @format GUID
   */
  _id?: string;
  /**
   * ID of the Wix account that purchased the subscription.
   * @format GUID
   */
  userId?: string;
  /**
   * ID of the [product](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/products/product-object)
   * for which the subscription was purchased.
   * @format GUID
   */
  productId?: string;
  /**
   * Date and time the subscription was created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  createdAt?: Date | null;
  /**
   * Date and time the subscription was last updated in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  updatedAt?: Date | null;
  /**
   * ID of the metasite that the subscription is assigned to.
   * Available only when the subscription is assigned to a Wix site.
   * Subscriptions for account level products can't be assigned to a Wix site.
   * @format GUID
   */
  metaSiteId?: string | null;
  /** Information about the system that manages the subscription's billing. */
  billingReference?: BillingReference;
  /** Information about the billing cycle of the subscription. */
  cycle?: Cycle;
  /**
   * Subscription status.
   *
   * + `UNKNOWN`: Default status.
   * + `AUTO_RENEW_ON`: Subscription is active and automatically renews at the end of the current billing cycle.
   * + `AUTO_RENEW_OFF`: Subscription is active but expires at the end of the current billing cycle.
   * + `MANUAL_RECURRING`: Subscription is active and renews at the end of the current billing cycle, in case the customer takes an action related to the payment.
   * + `CANCELLED`: Subscription isn't active because it has been canceled.
   * + `TRANSFERRED`: Subscription isn't active because it has been transferred to a different account. A different active subscription was created for the target account.
   */
  status?: SubscriptionStatusWithLiterals;
  /**
   * Date and time the subscription was last transferred from one Wix account to
   * another in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  transferredAt?: Date | null;
  /**
   * ID of the [product type](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/product-types/product-type-object)
   * that the product, for which the subscription was purchased, belongs to.
   * @format GUID
   */
  productTypeId?: string;
  /** Version number, which increments by 1 each time the subscription is updated. */
  version?: number;
  /**
   * Whether the subscription is active. Includes the statuses
   * `"AUTO_RENEW_ON"`, `"AUTO_RENEW_OFF"`, and `"MANUAL_RECURRING"`.
   */
  active?: boolean;
  /**
   * Date and time the subscription was originally created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * Differs from `createdAt` in case the subscription was originally created for a different Wix account and has been transferred.
   */
  originalCreationDate?: Date | null;
  /** Custom metadata about the subscription. */
  metadata?: Record<string, string>;
  /**
   * 2-letter country code in
   * [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements)
   * format.
   */
  countryCode?: string | null;
  /**
   * Seats of the product that the subscription was purchased for.
   * Will be Null for products that don't support seats.
   * @min 1
   * @max 500
   */
  seats?: number | null;
  /**
   * `true` if this subscription is part of a dependency relationship —
   * either as the main subscription or as a dependent.
   * @readonly
   */
  hasDependency?: boolean;
}

export interface BillingReference {
  /**
   * Name of the billing system that manages the subscription.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"SBS"`: [Wix Billing](https://github.com/wix-p/premium-billing/tree/master/sbs).
   * + `"LICENSER"`:
   * + `"BASS"`: [Billing and Subscriptions System](https://dev.wix.com/docs/rest/internal-only/premium/subscriptions-by-billing-by-wix/introduction).
   * + `"RESELLER"`: [External Reseller](https://dev.wix.com/api/rest/account-level-apis/resellers/introduction).
   */
  providerName?: ProviderNameWithLiterals;
  /** Current provider reference ID. */
  providerReferenceId?: string | null;
  /** Previous provider reference IDs. Used for when a subscription is extended, specifically for domains. */
  previousProviderReferenceIds?: string[];
}

export enum ProviderName {
  UNKNOWN = 'UNKNOWN',
  SBS = 'SBS',
  LICENSER = 'LICENSER',
  BASS = 'BASS',
  RESELLER = 'RESELLER',
  RECURRING_INVOICES = 'RECURRING_INVOICES',
}

/** @enumType */
export type ProviderNameWithLiterals =
  | ProviderName
  | 'UNKNOWN'
  | 'SBS'
  | 'LICENSER'
  | 'BASS'
  | 'RESELLER'
  | 'RECURRING_INVOICES';

export interface Cycle extends CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

/** @oneof */
export interface CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

export interface Interval {
  /** interval unit of measure */
  unit?: IntervalUnitWithLiterals;
  /** number of interval */
  count?: number;
}

export enum IntervalUnit {
  /** unknown interval unit */
  UNKNOWN = 'UNKNOWN',
  /** day */
  DAY = 'DAY',
  /** week */
  WEEK = 'WEEK',
  /** month */
  MONTH = 'MONTH',
  /** year */
  YEAR = 'YEAR',
}

/** @enumType */
export type IntervalUnitWithLiterals =
  | IntervalUnit
  | 'UNKNOWN'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface OneTime {}

export enum SubscriptionStatus {
  UNKNOWN = 'UNKNOWN',
  AUTO_RENEW_ON = 'AUTO_RENEW_ON',
  AUTO_RENEW_OFF = 'AUTO_RENEW_OFF',
  MANUAL_RECURRING = 'MANUAL_RECURRING',
  CANCELLED = 'CANCELLED',
  TRANSFERRED = 'TRANSFERRED',
}

/** @enumType */
export type SubscriptionStatusWithLiterals =
  | SubscriptionStatus
  | 'UNKNOWN'
  | 'AUTO_RENEW_ON'
  | 'AUTO_RENEW_OFF'
  | 'MANUAL_RECURRING'
  | 'CANCELLED'
  | 'TRANSFERRED';

/** Triggered when a subscription is reactivated. */
export interface ReactivationData {
  reactivationReason?: ReactivationReasonEnumWithLiterals;
  /**
   * In the event of reactivation after chargeback dispute, the subscription may be extended according to the
   * number of days it was inactive during the time of resolving the dispute
   */
  newEndOfPeriod?: Date | null;
  /** The original end date, before the inactive period. */
  oldEndOfPeriod?: Date | null;
  /** The difference in days between the new new_end_of_period and old_end_of_period */
  differenceInDays?: number | null;
}

/** Reason for subscription reactivation */
export enum ReactivationReasonEnum {
  UNKNOWN = 'UNKNOWN',
  /**
   * Subscription was reactivated due to billing status change from CANCELED to ACTIVE, for example if it was incorrectly
   * canceled because of suspicion of fraud
   */
  BILLING_STATUS_CHANGE = 'BILLING_STATUS_CHANGE',
  /** Subscription was reactivated after a chargeback dispute */
  REACTIVATED_AFTER_CHARGEBACK = 'REACTIVATED_AFTER_CHARGEBACK',
}

/** @enumType */
export type ReactivationReasonEnumWithLiterals =
  | ReactivationReasonEnum
  | 'UNKNOWN'
  | 'BILLING_STATUS_CHANGE'
  | 'REACTIVATED_AFTER_CHARGEBACK';

/**
 * Triggered when a subscription is assigned to a Wix site, including the initial
 * assignment of a floating subscription or a re-assignement from a different site.
 */
export interface SubscriptionAssigned {
  /** Assigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the update.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
}

/** Triggered when a subscription is canceled. */
export interface SubscriptionCancelled {
  /** Canceled subscription. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is canceled immediately or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
  /** Whether the subscription was canceled during the free trial period. */
  canceledInFreeTrial?: boolean;
  /** The type of refund applied to the cancellation. */
  refundType?: RefundTypeWithLiterals;
}

/** Information about the cancellation flow including who canceled the subscription and why it was canceled. */
export interface CancellationDetails {
  /**
   * Cancellation code.
   *
   * Values supported for cancellations on behalf of the billing system: `-1`, `-2`, `-3`, `-4`, `-5`, `-6`, `-7`, `-8`.
   * For cancellations on behalf of the site owner or the service provider `cancellationCode`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   *
   * + `-1`: The subscription has been cancelled by the billing system but none of the listed cancellation reasons applies.
   * + `-2`: There were payment problems.
   * + `-3`: There was a chargeback.
   * + `-4`: Customer support has canceled the subscription and issued a refund.
   * + `-5`: The site owner has changed their existing subscription.
   * + `-6`: The subscription has been transferred to a different Wix account.
   * + `-7`: The subscription has been canceled because the site owner hasn't manually authenticated the recurring payment during the subscription's grace period. For example, site owners must manually confirm recurring payments within 40 days when paying with boleto.
   * + `-8`: The Wix account that the subscription belonged to has been deleted.
   */
  cancellationCode?: number | null;
  /**
   * Cancellation reason. For cancellations on behalf of the site owner or the service provider `cancellationReason`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   * For cancellations on behalf of the billing system `cancellationReason` is `null` or an empty string.
   */
  cancellationReason?: string | null;
  /**
   * Initiator of the cancellation. For `"USER_REQUESTED"` and `"APP_MANAGED"`,
   * `cancellationCode` and `cancellationReason` are taken from the request of
   * [Cancel Immediately](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately)
   * or [Cancel Immediately Offline](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately-offline).
   * For `"PASSIVE"`, cancellations `cancellationCode` is automatically calculated and `cancellationReason`
   * is `null` or an empty string.
   *
   * + `"UNKNOWN`: Default value.
   * + `"USER_REQUESTED"`:  The Wix account owner has canceled the subscription.
   * + `"APP_MANAGED"`: The service provider has canceled the subscription.
   * + `"PASSIVE"`: The billing system has canceled the subscription. For example, in case of payment failure or fraud.
   */
  initiator?: InitiatorWithLiterals;
}

export enum Initiator {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  APP_MANAGED = 'APP_MANAGED',
  PASSIVE = 'PASSIVE',
}

/** @enumType */
export type InitiatorWithLiterals =
  | Initiator
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'APP_MANAGED'
  | 'PASSIVE';

export enum RefundType {
  UNKNOWN = 'UNKNOWN',
  NO_REFUND = 'NO_REFUND',
  FULL_REFUND = 'FULL_REFUND',
  PRORATED_REFUND = 'PRORATED_REFUND',
}

/** @enumType */
export type RefundTypeWithLiterals =
  | RefundType
  | 'UNKNOWN'
  | 'NO_REFUND'
  | 'FULL_REFUND'
  | 'PRORATED_REFUND';

/** Triggered when the subscription's auto renew is turned on. */
export interface SubscriptionAutoRenewTurnedOn {
  /** Subscription for which auto renew is turned on. */
  subscription?: Subscription;
  /**
   * Supported values: `USER`, `APP`.
   *
   * Information about who turned auto renew on.
   * + `"USER"`: The site owner who purchased the subscription has turned auto renew on.
   * + `"APP"`: The service provider has turned auto renew on.
   */
  initiator?: string | null;
}

/** Triggered when the subscription's auto renew is turned off. */
export interface SubscriptionAutoRenewTurnedOff {
  /** Subscription for which auto renew is turned off. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is immediately canceled or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
}

/**
 * Triggered when a subscription is unassigned from a Wix site and becomes
 * floating.
 */
export interface SubscriptionUnassigned {
  /** Unassigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the event.
   * @format GUID
   */
  previousMetaSiteId?: string;
  /**
   * Reason why the subscription is unassigned.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"USER_REQUESTED"`: The Wix account owner has unassigned the subscription.
   * + `"REPLACED_BY_ANOTHER_SUBSCRIPTION"`: A different subscription that replaces this subscription is assigned to the site.
   */
  unassignReason?: UnassignReasonWithLiterals;
}

export enum UnassignReason {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  REPLACED_BY_ANOTHER_SUBSCRIPTION = 'REPLACED_BY_ANOTHER_SUBSCRIPTION',
}

/** @enumType */
export type UnassignReasonWithLiterals =
  | UnassignReason
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'REPLACED_BY_ANOTHER_SUBSCRIPTION';

/**
 * Triggered when a subscription is transferred from one Wix account to another.
 * A transfer includes cancelling the original subscription and creating a new
 * subscription for the target account. The event returns both the original
 * and the new subscription.
 */
export interface SubscriptionTransferred {
  /** Original subscription that was canceled for the transfer. */
  originSubscription?: Subscription;
  /** Newly created subscription for the target account. */
  targetSubscription?: Subscription;
}

/** Triggered when a recurring charge succeeds for a subscription. */
export interface RecurringChargeSucceeded {
  /** Subscription for which the recurring charge has succeeded. */
  subscription?: Subscription;
  /** Indication that there was a successful charge at the end of the free trial period */
  freeTrialPeriodEnd?: boolean;
}

/**
 * Triggered when a subscription was updated including when its product has been
 * up- or downgraded or the billing cycle is changed.
 */
export interface ContractSwitched {
  /** Updated subscription. */
  subscription?: Subscription;
  /** Billing cycle before the update. */
  previousCycle?: Cycle;
  /**
   * ID of the product belonging to the subscription before the update.
   * @format GUID
   */
  previousProductId?: string;
  /**
   * ID of the product type that the subscription's original product belonged to before the update.
   * @format GUID
   */
  previousProductTypeId?: string;
  /**
   * Update type. __Note__: Doesn't include information about a product adjustment.
   * For that purpose, see `productAdjustment`.
   *
   * + `"NOT_APPLICABLE"`: Default value.
   * + `"ADDITIONAL_QUANTITY"`: An increased usage quota is added to the subscription. For example, a second mailbox is added to a subscription that previously included a single mailbox.
   * + `"CREDIT_UNUSED_PERIOD"`: The subscription is upgraded and the new price is less than the regular price. The new price applies to every billing cycle, not just the first cycle.
   * + `"REFUND_PRICE_DIFF"`: Not implemented.
   * + `"ADJUST_PERIOD_END"`: Not implemented.
   * + `"DOWNGRADE_GRACE_PERIOD"`: For downgrades during the grace period. In this situation, the site owner hasn’t paid yet and must immediately pay for the downgraded subscription.
   * + `"FULL_AMOUNT_PERIOD"`: For upgrades in which the site owner retains unused benefits. For example, site owners upgrading a Facebook Ads subscription retain their unused FB Ads credit. The unused credit is added to the new credit.
   * + `"END_OF_PERIOD"`: The subscription's billing current cycle is extended because of a downgrade.
   * + `"PENDING_CHANGES"`: The subscription's billing is updated, but the change doesn't apply immediately. Instead, the update becomes effective at the end of current billing cycle.
   * + `"DOWNGRADE_RENEWAL"`: The subscription is downgraded because of a declined payment. This prevents subscriptions from churning.
   */
  contractSwitchType?: ContractSwitchTypeWithLiterals;
  /**
   * ID of the metasite the subscription has been assigned to previously.
   * Available only in case the subscription is assigned to a different site.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
  /**
   * Update reason.
   *
   * + `"PRICE_INCREASE"`: The subscription's price has been increased.
   * + `"EXTERNAL_PROVIDER_TRIGGER"`: Any reason other than a price increase.
   */
  contractSwitchReason?: ContractSwitchReasonWithLiterals;
  /** Information about the price update. Available only for updates with a price increase. */
  productPriceIncreaseData?: ProductPriceIncreaseData;
  /**
   * Information about a product adjustment. For example, a downgrade.
   * __Note__: This isn't the same as `contractSwitchType`.
   *
   * + `NOT_APPLICABLE`: There is no information about whether the product has been up- or downgraded.
   * + `DOWNGRADE`: The product has been downgraded.
   */
  productAdjustment?: ProductAdjustmentWithLiterals;
  /**
   * Number of seats before the contract switch.
   * @min 1
   * @max 500
   */
  previousSeats?: number | null;
}

/** Copied from SBS */
export enum ContractSwitchType {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  ADDITIONAL_QUANTITY = 'ADDITIONAL_QUANTITY',
  CREDIT_UNUSED_PERIOD = 'CREDIT_UNUSED_PERIOD',
  REFUND_PRICE_DIFF = 'REFUND_PRICE_DIFF',
  ADJUST_PERIOD_END = 'ADJUST_PERIOD_END',
  DOWNGRADE_GRACE_PERIOD = 'DOWNGRADE_GRACE_PERIOD',
  FULL_AMOUNT_PERIOD = 'FULL_AMOUNT_PERIOD',
  END_OF_PERIOD = 'END_OF_PERIOD',
  PENDING_CHANGES = 'PENDING_CHANGES',
  DOWNGRADE_RENEWAL = 'DOWNGRADE_RENEWAL',
  FIXED_BILLING_DATE_PRORATION = 'FIXED_BILLING_DATE_PRORATION',
  IMMEDIATE_SWITCH = 'IMMEDIATE_SWITCH',
  FLAT_PRICE_DIFFERENCE = 'FLAT_PRICE_DIFFERENCE',
}

/** @enumType */
export type ContractSwitchTypeWithLiterals =
  | ContractSwitchType
  | 'NOT_APPLICABLE'
  | 'ADDITIONAL_QUANTITY'
  | 'CREDIT_UNUSED_PERIOD'
  | 'REFUND_PRICE_DIFF'
  | 'ADJUST_PERIOD_END'
  | 'DOWNGRADE_GRACE_PERIOD'
  | 'FULL_AMOUNT_PERIOD'
  | 'END_OF_PERIOD'
  | 'PENDING_CHANGES'
  | 'DOWNGRADE_RENEWAL'
  | 'FIXED_BILLING_DATE_PRORATION'
  | 'IMMEDIATE_SWITCH'
  | 'FLAT_PRICE_DIFFERENCE';

export enum ContractSwitchReason {
  EXTERNAL_PROVIDER_TRIGGER = 'EXTERNAL_PROVIDER_TRIGGER',
  PRICE_INCREASE = 'PRICE_INCREASE',
}

/** @enumType */
export type ContractSwitchReasonWithLiterals =
  | ContractSwitchReason
  | 'EXTERNAL_PROVIDER_TRIGGER'
  | 'PRICE_INCREASE';

/** Triggered when a subscription's price is increased. */
export interface ProductPriceIncreaseData {
  /**
   * Price of the subscription before the update.
   * @format DECIMAL_VALUE
   */
  previousPrice?: string | null;
  /** A value that is used in order to select the correct email template to send the user regarding the price increase. */
  emailTemplateSelector?: string | null;
  /** Used to differentiate between migration segments. Does not have to be unique per segment. */
  segmentName?: string | null;
  /** Used to determine how the price increase was triggered. */
  priceIncreaseTrigger?: PriceIncreaseTriggerWithLiterals;
}

/** Reason for Price Increase Trigger */
export enum PriceIncreaseTrigger {
  NEAR_RENEWAL = 'NEAR_RENEWAL',
  RECURRING_SUCCESS = 'RECURRING_SUCCESS',
  MANUAL = 'MANUAL',
}

/** @enumType */
export type PriceIncreaseTriggerWithLiterals =
  | PriceIncreaseTrigger
  | 'NEAR_RENEWAL'
  | 'RECURRING_SUCCESS'
  | 'MANUAL';

/** Triggered when a subscription's product is adusted. */
export enum ProductAdjustment {
  /** flag to show that the ContractSwitchedEvent is not applicable / needed */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  /** flag to show that the ContractSwitchedEvent is a Downgrade */
  DOWNGRADE = 'DOWNGRADE',
}

/** @enumType */
export type ProductAdjustmentWithLiterals =
  | ProductAdjustment
  | 'NOT_APPLICABLE'
  | 'DOWNGRADE';

/**
 * Triggered when a subscription gets close to the end of its billing cycle.
 * The exact number of days is defined in the billing system.
 */
export interface SubscriptionNearEndOfPeriod {
  /** Subscription that got close to the end of its billing cycle. */
  subscription?: Subscription;
  /** Whether the subscription is within the free trial period. */
  inFreeTrial?: boolean;
}

/**
 * Triggered when a subscription is updated and the change doesn't happen
 * immediately but at the end of the current billing cycle.
 */
export interface SubscriptionPendingChange {
  /** Subscription for which a pending update is triggered. */
  subscription?: Subscription;
}

/** Triggered when a recurring charge attempt failed for a subscription. */
export interface RecurringChargeAttemptFailed {
  /** Subscription for which the recurring charge attempt has failed. */
  subscription?: Subscription;
  /** Reason the recurring charge attempt failed or could not be made. */
  reason?: RecurringChargeFailureReasonWithLiterals;
  /**
   * ID of the invoice associated with the recurring charge failure.
   * @maxLength 36
   */
  invoiceId?: string | null;
}

export enum RecurringChargeFailureReason {
  CHARGE_FAILURE = 'CHARGE_FAILURE',
  CREDIT_CARD_EXPIRED = 'CREDIT_CARD_EXPIRED',
  NO_ACTIVE_BILLING_ACCOUNT = 'NO_ACTIVE_BILLING_ACCOUNT',
}

/** @enumType */
export type RecurringChargeFailureReasonWithLiterals =
  | RecurringChargeFailureReason
  | 'CHARGE_FAILURE'
  | 'CREDIT_CARD_EXPIRED'
  | 'NO_ACTIVE_BILLING_ACCOUNT';

/** Triggered when a subscription is updated. */
export interface SubscriptionUpdated {
  /** Updated subscription. */
  subscription?: Subscription;
}

/** Triggered when a Vista reseller subscription is migrated into Wix SBS billing. */
export interface SubscriptionMigratedFromVista {
  /** The Vista migration ID for this subscription (returned by the Vista API). */
  vistaMigrationId?: string;
  /** The new SBS service ID created for this subscription. */
  newSbsServiceId?: string;
  /** Full subscription context for owner extraction and notifications. */
  subscription?: Subscription;
  /** Date and time of the next scheduled billing charge. */
  nextBillingDate?: Date | null;
  /** Amount to be charged on the next billing date (decimal string, e.g. "9.99"). */
  nextBillingAmount?: string;
  /** ISO 4217 currency code for the billing amount (e.g. "USD"). */
  currency?: string;
  /**
   * Date and time when billing officially transitions from VistaPrint to Wix.
   * Used in notification emails as the effective date of the billing changeover.
   */
  effectiveDate?: Date | null;
  /** The original Wix product ID from Vista (before migration mapping). */
  previousWixProductId?: string;
  /** The new Wix product ID after applying the Vista product mapping. */
  newWixProductId?: string;
}

/**
 * Triggered when a Vista migration is reverted, restoring the original
 * reseller billing provider and product.
 */
export interface SubscriptionVistaMigrationReverted {
  /** The reverted subscription with its original billing provider and product restored. */
  subscription?: Subscription;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /**
   * AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default.
   * Not the dev-center dev site (see Flag.DEV_CENTER_DEV_SITE_FLAG).
   */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
  /**
   * Symphony — a siteless site representing a project within Symphony (the evolution of Orion).
   * Holds project data, conversations, assets, and manages collaborators / shared team members for the project.
   */
  SYMPHONY = 'SYMPHONY',
  /** Nautilus platform app. */
  NAUTILUS_APPS = 'NAUTILUS_APPS',
  /**
   * Vibu — a siteless site representing a project built with Vibu (the AI app builder).
   * Holds the project's generated code, conversations, assets, and manages collaborators for the project.
   */
  VIBU = 'VIBU',
  /**
   * Ghost site for the BrandKit multi-site platform. Each ghost backs a single BrandKit
   * (an Enterprise account may have several), holding that BrandKit's brand definition
   * (theme, typography, assets) which is rolled out to the BrandKit's connected sites.
   * MetaSites with this namespace will *not* be shown in a user's site list by default.
   */
  ENTERPRISE_BRAND_KIT = 'ENTERPRISE_BRAND_KIT',
  /**
   * Dev-center development sites (com.wixpress.devcenter.dsm).
   * DEPRECATED: replaced by Flag.DEV_CENTER_DEV_SITE_FLAG (a dev site is orthogonal to the namespace). Do not use for new sites.
   */
  DEV_CENTER_DEV_SITE = 'DEV_CENTER_DEV_SITE',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS'
  | 'SYMPHONY'
  | 'NAUTILUS_APPS'
  | 'VIBU'
  | 'ENTERPRISE_BRAND_KIT'
  | 'DEV_CENTER_DEV_SITE';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

/**
 * Retrieves a site's Pricing Plans Settings.
 * @public
 * @documentationMaturity preview
 * @permissionId PRICING_PLANS.PRICING_PLANS_SETTINGS_READ
 * @applicableIdentity APP
 * @returns Pricing Plans settings
 * @fqn wix.pricing_plans.settings.v1.PricingPlansSettingsService.GetPricingPlansSettings
 */
export async function getPricingPlansSettings(): Promise<
  NonNullablePaths<PricingPlansSettings, `taxableAddress`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixPricingPlansSettingsV1PricingPlansSettings.getPricingPlansSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.pricingPlansSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a site's Pricing Plans settings.
 *
 * Each time the Pricing Plans settings are updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the Pricing Plans settings.
 * This ensures you're working with the latest Pricing Plans settings and prevents unintended overwrites.
 * @param pricingPlansSettings - PricingPlansSettings to be updated, may be partial.
 * @public
 * @documentationMaturity preview
 * @requiredField pricingPlansSettings
 * @requiredField pricingPlansSettings.revision
 * @permissionId PRICING_PLANS.PRICING_PLANS_SETTINGS_UPDATE
 * @applicableIdentity APP
 * @returns Updated PricingPlansSettings.
 * @fqn wix.pricing_plans.settings.v1.PricingPlansSettingsService.UpdatePricingPlansSettings
 */
export async function updatePricingPlansSettings(
  pricingPlansSettings: NonNullablePaths<PricingPlansSettings, `revision`, 2>
): Promise<NonNullablePaths<PricingPlansSettings, `taxableAddress`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pricingPlansSettings: pricingPlansSettings,
  });

  const reqOpts =
    ambassadorWixPricingPlansSettingsV1PricingPlansSettings.updatePricingPlansSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.pricingPlansSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pricingPlansSettings: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pricingPlansSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
